import React, { useState, useMemo } from 'react';
import { ShoppingCart, Heart, Eye, ShieldCheck, Flame, Award, ArrowUpRight, Sparkles, CheckCircle2 } from 'lucide-react';
import { useCart, useWishlist, useCurrency } from '../../contexts/SiteContexts';
import { cn } from '../../lib/utils';
import { ImageWithFallback } from '../ui/ImageWithFallback';
import { calculateProductTiers, PricingTierKey } from '../../utils/pricingTiers';

interface ProductCardProps {
  item: any;
  idx: number;
  setSelectedItem: (item: any) => void;
  addToCart: (item: any, quantity?: number) => void;
  formatAllPrices: (price: number) => any;
  t: (key: string) => string;
}

const ProductCardComponent: React.FC<ProductCardProps> = ({ 
  item, 
  idx, 
  setSelectedItem, 
  addToCart, 
  formatAllPrices, 
  t 
}) => {
  const { toggleWishlist, isInWishlist } = useWishlist();
  const { formatPrice } = useCurrency();

  // 3-Tier Pricing: Normal (-10%), Premium (Official Base), VIP (+10% to +20%)
  const tiers = useMemo(() => calculateProductTiers(item), [
    item.price, 
    item.normalPrice, 
    item.premiumPrice, 
    item.vipPrice, 
    item.vipMargin
  ]);
  const [selectedTier, setSelectedTier] = useState<PricingTierKey>('premium');

  const currentTier = tiers[selectedTier];
  const activePrice = currentTier.priceUsd;
  const prices = formatAllPrices(activePrice);

  const handleAddToCart = (e: React.MouseEvent) => {
    e.stopPropagation();
    addToCart({
      ...item,
      price: activePrice,
      selectedTier,
      tierLabel: currentTier.labelEn
    });
  };

  return (
    <div 
      id={`product-card-${item.id}`}
      style={{ contentVisibility: 'auto', containIntrinsicSize: '0 520px' }}
      className="group bg-white rounded-3xl border border-slate-100 p-4 sm:p-5 hover:shadow-2xl hover:shadow-indigo-500/10 hover:border-indigo-100 transition-all duration-300 flex flex-col h-full relative min-h-[520px]"
    >
      {/* 1. Header Section ABOVE the picture: Category + Origin */}
      <div className="flex items-center justify-between gap-2 mb-3 pb-1 border-b border-slate-50">
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="w-2 h-2 rounded-full bg-indigo-600 shrink-0" />
          
          <span className="text-[11px] font-black uppercase tracking-widest text-indigo-600 truncate">
            {item.category || 'Surgical'}
            {item.subcategory && (
              <span className="text-slate-400 font-bold ml-1">/ {item.subcategory}</span>
            )}
          </span>
        </div>
        
        {/* Made by Hamim / Origin Badge ABOVE the picture */}
        <span className="px-2.5 py-1 bg-slate-900 text-white rounded-full text-[9px] font-black uppercase tracking-wider shrink-0 flex items-center gap-1 shadow-sm">
          <Award size={10} className="text-amber-400" />
          <span>{item.origin || 'Made by Hamim'}</span>
        </span>
      </div>

      {/* 2. Product Picture Container: 100% Clean Image Stage */}
      <div 
        onClick={() => setSelectedItem(item)}
        className="relative aspect-square mb-3 overflow-hidden rounded-2xl bg-slate-50 border border-slate-100/80 cursor-pointer flex items-center justify-center p-0 group/img"
      >
        <ImageWithFallback 
          src={item.imageUrl} 
          alt={item.name} 
          wrapperClassName="w-full h-full" 
          className="w-full h-full object-contain group-hover/img:scale-105 transition-transform duration-500" 
          loading={idx < 6 ? "eager" : "lazy"}
          priority={idx < 6}
          // @ts-ignore
          fetchPriority={idx < 4 ? "high" : "auto"}
          referrerPolicy="no-referrer" 
        />
        
        {/* Subtle quick action hover icons in top-right corner only */}
        <div className="absolute top-2.5 right-2.5 flex flex-col gap-1.5 opacity-0 group-hover/img:opacity-100 transition-opacity duration-300 z-10" onClick={(e) => e.stopPropagation()}>
          <button 
            onClick={() => toggleWishlist(item.id)}
            className={cn(
              "p-2 rounded-xl shadow-md backdrop-blur-md transition-all active:scale-90",
              isInWishlist(item.id) ? "bg-red-500 text-white" : "bg-white/95 text-slate-600 hover:bg-white hover:text-red-500"
            )}
            title="Add to Wishlist"
          >
            <Heart size={14} fill={isInWishlist(item.id) ? "currentColor" : "none"} />
          </button>
          <button 
            onClick={() => setSelectedItem(item)}
            className="p-2 bg-white/95 hover:bg-white text-slate-600 rounded-xl shadow-md active:scale-90 transition-all"
            title="Inspect Details"
          >
            <Eye size={14} />
          </button>
        </div>
      </div>

      {/* 3. Badges moved BELOW the picture (Autoclave & Steel Material) */}
      <div className="flex items-center gap-1.5 mb-2.5 flex-wrap">
        <div className="px-2 py-0.5 bg-amber-50 text-amber-900 border border-amber-200/80 rounded-md text-[9px] font-black uppercase tracking-wider flex items-center gap-1 shadow-2xs">
          <Flame size={11} className="text-amber-600" />
          <span>134°C Autoclave Safe</span>
        </div>
        <div className="px-2 py-0.5 bg-slate-100 text-slate-700 border border-slate-200/70 rounded-md text-[9px] font-black uppercase tracking-wider flex items-center gap-1 truncate shadow-2xs">
          <ShieldCheck size={11} className="text-emerald-600" />
          <span className="truncate">{item.material || 'AISI 410 German Steel'}</span>
        </div>
      </div>

      {/* 4. Full Complete Product Name & Clinical Description */}
      <div className="flex-1 flex flex-col cursor-pointer mb-3" onClick={() => setSelectedItem(item)}>
        <h3 className="font-black text-slate-900 uppercase tracking-tight text-sm sm:text-base leading-snug group-hover:text-indigo-600 transition-colors mb-1 line-clamp-2 min-h-[2.5rem]">
          {item.name}
        </h3>

        <p className="text-xs text-slate-500 font-medium leading-relaxed line-clamp-2 mb-2 min-h-[2rem]">
          {item.description || "Precision engineered surgical instrument with passivated anti-glare finish, designed for sterile operating theatre procedures."}
        </p>

        <div className="flex items-center gap-2 text-[10px] text-slate-400 font-mono font-bold mt-auto">
          <span>CODE: {item.code || item.id?.slice(0, 8)}</span>
          {item.size && <span>• {item.size}</span>}
        </div>
      </div>

      {/* 5. 3-TIER PRICING SELECTOR: Normal, Premium, VIP (Clean without Urdu and without % margins) */}
      <div className="mt-auto pt-3 border-t border-slate-100 space-y-2.5">
        
        {/* Tier Buttons Bar */}
        <div className="bg-slate-50 p-1 rounded-2xl border border-slate-200 grid grid-cols-3 gap-1">
          {/* Normal Tier */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedTier('normal');
            }}
            className={cn(
              "py-2 px-1 rounded-xl text-center transition-all flex flex-col items-center justify-center cursor-pointer",
              selectedTier === 'normal' 
                ? "bg-white text-slate-900 shadow-sm border border-slate-200" 
                : "text-slate-600 hover:text-slate-900"
            )}
            title="Normal Price"
          >
            <span className="text-[10px] font-black uppercase tracking-wider text-slate-800">
              Normal
            </span>
            <span className="text-xs font-black mt-0.5 text-slate-900">
              ${tiers.normal.priceUsd.toFixed(2)}
            </span>
          </button>

          {/* Premium Tier (Official Price) */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedTier('premium');
            }}
            className={cn(
              "py-2 px-1 rounded-xl text-center transition-all flex flex-col items-center justify-center cursor-pointer",
              selectedTier === 'premium' 
                ? "bg-indigo-600 text-white shadow-md shadow-indigo-200" 
                : "text-slate-600 hover:text-slate-900"
            )}
            title="Premium Price"
          >
            <span className={cn("text-[10px] font-black uppercase tracking-wider", selectedTier === 'premium' ? "text-white" : "text-slate-800")}>
              Premium
            </span>
            <span className={cn("text-xs font-black mt-0.5", selectedTier === 'premium' ? "text-white" : "text-indigo-900")}>
              ${tiers.premium.priceUsd.toFixed(2)}
            </span>
          </button>

          {/* VIP Tier */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedTier('vip');
            }}
            className={cn(
              "py-2 px-1 rounded-xl text-center transition-all flex flex-col items-center justify-center cursor-pointer",
              selectedTier === 'vip' 
                ? "bg-slate-900 text-amber-400 shadow-md shadow-slate-900/30" 
                : "text-slate-600 hover:text-slate-900"
            )}
            title="VIP Price"
          >
            <span className="text-[10px] font-black uppercase tracking-wider flex items-center gap-1">
              <span>VIP</span>
              <Sparkles size={10} className="text-amber-400 shrink-0" />
            </span>
            <span className={cn("text-xs font-black mt-0.5", selectedTier === 'vip' ? "text-white" : "text-slate-900")}>
              ${tiers.vip.priceUsd.toFixed(2)}
            </span>
          </button>
        </div>

        {/* Selected Tier Banner & Converted Currencies */}
        <div className="flex items-baseline justify-between pt-1">
          <div className="flex items-baseline gap-2">
            <span className="text-lg sm:text-xl font-black text-slate-900">{prices.usd}</span>
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
              {currentTier.labelEn}
            </span>
          </div>
          <span className={cn(
            "text-[9px] font-black px-2.5 py-0.5 rounded-md uppercase tracking-wider",
            selectedTier === 'normal' && "text-emerald-700 bg-emerald-50 border border-emerald-200",
            selectedTier === 'premium' && "text-indigo-700 bg-indigo-50 border border-indigo-200",
            selectedTier === 'vip' && "text-amber-800 bg-amber-50 border border-amber-200"
          )}>
            {currentTier.labelEn}
          </span>
        </div>

        {/* Dual Currency PKR & CNY Display */}
        <div className="grid grid-cols-2 gap-1.5 py-1.5 px-2.5 bg-slate-50 rounded-xl border border-slate-100 text-[10px] font-bold text-slate-600">
          <div className="flex items-center gap-1">
            <span className="text-slate-400 font-normal">PKR:</span>
            <span className="font-black text-slate-900 truncate">{prices.pkr}</span>
          </div>
          <div className="flex items-center gap-1 justify-end">
            <span className="text-slate-400 font-normal">CNY:</span>
            <span className="font-black text-slate-900 truncate">{prices.cny}</span>
          </div>
        </div>

        {/* Action Buttons: Full Width Add to Cart & Quick View */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 pt-1">
          <button 
            id={`add-cart-btn-${item.id}`}
            onClick={handleAddToCart}
            className={cn(
              "sm:col-span-3 flex items-center justify-center gap-2 py-3 rounded-xl text-xs font-black uppercase tracking-widest transition-all shadow-md active:scale-95 cursor-pointer",
              selectedTier === 'vip' 
                ? "bg-slate-900 hover:bg-black text-white shadow-slate-900/20" 
                : "bg-indigo-600 hover:bg-slate-950 text-white shadow-indigo-100"
            )}
          >
            <ShoppingCart size={14} />
            <span>Add to Cart ({currentTier.labelEn})</span>
          </button>
          
          <button 
            id={`inspect-btn-${item.id}`}
            onClick={(e) => {
              e.stopPropagation();
              setSelectedItem(item);
            }}
            className="sm:col-span-1 flex items-center justify-center p-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl transition-all active:scale-95 cursor-pointer"
            title="Full Specs & All 3 Tiers"
          >
            <ArrowUpRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

export const ProductCard = React.memo(ProductCardComponent, (prevProps, nextProps) => {
  return (
    prevProps.item.id === nextProps.item.id &&
    prevProps.item.price === nextProps.item.price &&
    prevProps.item.normalPrice === nextProps.item.normalPrice &&
    prevProps.item.premiumPrice === nextProps.item.premiumPrice &&
    prevProps.item.vipPrice === nextProps.item.vipPrice &&
    prevProps.item.stock === nextProps.item.stock &&
    prevProps.idx === nextProps.idx
  );
});